import React, { createContext, useContext, useState, useEffect, ReactNode } from 'react';
import {
  getCookieConsentStatus,
  getCookiePreferences,
  saveCookieConsent,
  initializeCookies,
  deleteNonEssentialCookies,
  CookiePreferences,
  CookieConsentStatus,
} from '../utils/cookieUtils';
import { initializeAnalytics, disableAnalytics } from '../services/analyticsService';

interface CookieConsentContextType {
  consentStatus: CookieConsentStatus;
  preferences: CookiePreferences | null;
  hasConsented: boolean;
  isAnalyticsAllowed: boolean;
  acceptAll: () => void;
  rejectAll: () => void;
  savePreferences: (preferences: CookiePreferences) => void;
  showConsent: boolean;
  setShowConsent: (show: boolean) => void;
}

const CookieConsentContext = createContext<CookieConsentContextType | undefined>(undefined);

export const CookieConsentProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
  const [consentStatus, setConsentStatus] = useState<CookieConsentStatus>(null);
  const [preferences, setPreferences] = useState<CookiePreferences | null>(null);
  const [showConsent, setShowConsent] = useState(false);

  // Load consent status on mount
  useEffect(() => {
    const status = getCookieConsentStatus();
    const prefs = getCookiePreferences();

    setConsentStatus(status);
    setPreferences(prefs);

    // Show consent popup if no consent has been given
    if (!status) {
      setShowConsent(true);
    } else if (prefs) {
      // Initialize cookies based on saved preferences
      initializeCookies(prefs);
      
      // Initialize analytics if allowed
      if (prefs.analytics) {
        initializeAnalytics();
      } else {
        disableAnalytics();
        deleteNonEssentialCookies();
      }
    }
  }, []);

  const acceptAll = () => {
    const newPreferences: CookiePreferences = {
      essential: true,
      analytics: true,
    };
    
    saveCookieConsent('accepted', newPreferences);
    setConsentStatus('accepted');
    setPreferences(newPreferences);
    setShowConsent(false);
    
    // Initialize cookies and analytics
    initializeCookies(newPreferences);
    initializeAnalytics();
  };

  const rejectAll = () => {
    const newPreferences: CookiePreferences = {
      essential: true, // Essential cookies are always true
      analytics: false,
    };
    
    saveCookieConsent('rejected', newPreferences);
    setConsentStatus('rejected');
    setPreferences(newPreferences);
    setShowConsent(false);
    
    // Initialize cookies (analytics will be disabled)
    initializeCookies(newPreferences);
    disableAnalytics();
    deleteNonEssentialCookies();
  };

  const savePreferences = (newPreferences: CookiePreferences) => {
    saveCookieConsent('custom', newPreferences);
    setConsentStatus('custom');
    setPreferences(newPreferences);
    setShowConsent(false);
    
    // Initialize cookies based on preferences
    initializeCookies(newPreferences);
    
    // Handle analytics
    if (newPreferences.analytics) {
      initializeAnalytics();
    } else {
      disableAnalytics();
      deleteNonEssentialCookies();
    }
  };

  const hasConsented = consentStatus === 'accepted' || consentStatus === 'custom';
  const isAnalyticsAllowed = 
    consentStatus === 'accepted' || 
    (consentStatus === 'custom' && preferences?.analytics === true);

  return (
    <CookieConsentContext.Provider
      value={{
        consentStatus,
        preferences,
        hasConsented,
        isAnalyticsAllowed,
        acceptAll,
        rejectAll,
        savePreferences,
        showConsent,
        setShowConsent,
      }}
    >
      {children}
    </CookieConsentContext.Provider>
  );
};

export const useCookieConsent = (): CookieConsentContextType => {
  const context = useContext(CookieConsentContext);
  if (context === undefined) {
    throw new Error('useCookieConsent must be used within a CookieConsentProvider');
  }
  return context;
};

